import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Loader2, CheckCircle, XCircle } from 'lucide-react';
import { API_CONFIG } from '@/lib/api/config';

export default function ShopifyCallback() {
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [message, setMessage] = useState<string>('Processing authorization...');

  useEffect(() => {
    const handleShopifyCallback = async () => {
      try {
        // Get URL parameters
        const urlParams = new URLSearchParams(window.location.search);
        const code = urlParams.get('code');
        const state = urlParams.get('state');
        const shop = urlParams.get('shop');
        const error = urlParams.get('error');
        const errorDescription = urlParams.get('error_description');

        // Check for OAuth errors
        if (error) {
          const errorMsg = errorDescription || error;

          // Send error to opener window if it exists
          if (window.opener && !window.opener.closed) {
            window.opener.postMessage({
              success: false,
              error: errorMsg,
              source: 'shopify-oauth'
            }, window.location.origin);
          }

          setStatus('error');
          setMessage(`Authorization failed: ${errorMsg}`);

          setTimeout(() => {
            window.close();
          }, 3000);
          return;
        }

        // Check for required parameters
        if (!code || !state || !shop) {
          const errorMsg = 'Missing required OAuth parameters';

          if (window.opener && !window.opener.closed) {
            window.opener.postMessage({
              success: false,
              error: errorMsg,
              source: 'shopify-oauth'
            }, window.location.origin);
          }

          setStatus('error');
          setMessage(errorMsg);

          setTimeout(() => {
            window.close();
          }, 3000);
          return;
        }

        setMessage('Exchanging authorization code for access token...');

        // Exchange code for access token via backend
        // Route is registered at both /api/v1/shopify/oauth/callback and /shopify/oauth/callback
        // Try /api/v1 version first (since API_CONFIG.BASE_URL includes /api/v1)
        const callbackUrl = `${API_CONFIG.BASE_URL}/shopify/oauth/callback`;

        const exchangeRes = await fetch(callbackUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            code,
            state,
            shop,
          }),
        });


        // Read response body once
        let exchangeData;
        const contentType = exchangeRes.headers.get('content-type');
        const isJson = contentType && contentType.includes('application/json');

        try {
          if (isJson) {
            exchangeData = await exchangeRes.json();
          } else {
            const text = await exchangeRes.text();
            // Try to parse as JSON even if content-type doesn't say so
            try {
              exchangeData = JSON.parse(text);
            } catch {
              // Not JSON, treat as text error
              throw new Error(text || `Server returned ${exchangeRes.status}: ${exchangeRes.statusText}`);
            }
          }
        } catch (parseError: any) {
          const errorMsg = parseError.message || 'Failed to parse server response. The server may be experiencing issues.';

          if (window.opener && !window.opener.closed) {
            window.opener.postMessage({
              success: false,
              error: errorMsg,
              source: 'shopify-oauth'
            }, window.location.origin);
          }

          setStatus('error');
          setMessage(`Failed: ${errorMsg}`);

          setTimeout(() => {
            window.close();
          }, 3000);
          return;
        }


        // Check if response indicates an error
        if (!exchangeRes.ok || !exchangeData.ok) {
          const errorMsg = exchangeData.error || exchangeData.message || `Server returned ${exchangeRes.status}: ${exchangeRes.statusText}`;

          if (window.opener && !window.opener.closed) {
            window.opener.postMessage({
              success: false,
              error: errorMsg,
              source: 'shopify-oauth'
            }, window.location.origin);
          }

          setStatus('error');
          setMessage(`Failed: ${errorMsg}`);

          setTimeout(() => {
            window.close();
          }, 3000);
          return;
        }

        setStatus('success');
        setMessage('Authorization successful! You can close this window.');

        // Send success to opener window
        if (window.opener && !window.opener.closed) {
          window.opener.postMessage({
            success: true,
            source: 'shopify-oauth',
            shop: exchangeData.shop
          }, window.location.origin);
        }

        // Close the popup after a short delay
        setTimeout(() => {
          window.close();
        }, 2000);
      } catch (error: any) {
        const errorMsg = error.message || 'An unexpected error occurred';

        if (window.opener && !window.opener.closed) {
          window.opener.postMessage({
            success: false,
            error: errorMsg,
            source: 'shopify-oauth'
          }, window.location.origin);
        }

        setStatus('error');
        setMessage(`Error: ${errorMsg}`);

        setTimeout(() => {
          window.close();
        }, 3000);
      }
    };

    handleShopifyCallback();
  }, []);

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle>Shopify Authorization</CardTitle>
          <CardDescription>Processing your authorization request...</CardDescription>
        </CardHeader>
        <CardContent className="flex flex-col items-center justify-center py-8 space-y-4">
          {status === 'loading' && (
            <>
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
              <p className="text-sm text-muted-foreground text-center">{message}</p>
            </>
          )}
          {status === 'success' && (
            <>
              <CheckCircle className="h-8 w-8 text-green-600" />
              <p className="text-sm text-muted-foreground text-center">{message}</p>
            </>
          )}
          {status === 'error' && (
            <>
              <XCircle className="h-8 w-8 text-destructive" />
              <p className="text-sm text-destructive text-center">{message}</p>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

